/**
 * Conversations realtime store (iter135.11 — Phase A)
 * ──────────────────────────────────────────────────
 *
 * Tiny external store backing the conversation UI. Replaces the
 * flat ``useState`` arrays at the top of ``ConversationsV2Page.jsx``
 * with a normalised, version-counter-driven shape that React can
 * subscribe to slice-by-slice via ``useSyncExternalStore``.
 *
 * Why an external store and not Redux/Zustand?
 *
 *   - No new dep, no boilerplate.
 *   - We only need ~10 mutation entry points and ~7 selectors.
 *   - The contract is *purely additive* over the current page —
 *     existing optimistic flows still work, they just call store
 *     mutations instead of ``setMessages([...prev, msg])``.
 *
 * The selective-subscription model is the whole point: a typing tick
 * for conv X bumps ``versions.typingByConv[X]`` ONLY → only
 * components subscribed to that conv's typing slice re-render. The
 * sidebar, message list, composer, and bubbles for OTHER conversations
 * see no change in their snapshot identity → no re-render at all.
 *
 *
 * State shape
 * ───────────
 *   convs: Map<conv_id, ConvMeta>
 *   convOrder: conv_id[]                    // newest first
 *   msgs: Map<conv_id, Map<msg_id, Msg>>
 *   msgOrder: Map<conv_id, msg_id[]>       // chronological
 *   typing: Map<conv_id, Map<userKey, isoTickAt>>
 *   reads: Map<conv_id, Map<userKey, lastSeq>>
 *   selectedConvId: string | null
 *
 * Version counters (for snapshot identity stability):
 *   versions.convs                         // bumps on convs/convOrder mutation
 *   versions.msgsByConv: Map<conv_id, n>
 *   versions.typingByConv: Map<conv_id, n>
 *   versions.readsByConv: Map<conv_id, n>
 *   versions.selected
 */

const _userKey = (userType, userId) => `${userType}:${userId}`;

class ConvStore {
  constructor() {
    this.state = {
      convs: new Map(),
      convOrder: [],
      msgs: new Map(),
      msgOrder: new Map(),
      typing: new Map(),
      reads: new Map(),
      selectedConvId: null,
    };
    this.versions = {
      convs: 0,
      msgsByConv: new Map(),
      typingByConv: new Map(),
      readsByConv: new Map(),
      selected: 0,
    };
    this._listeners = new Set();
    // Snapshot caches keyed by version so ``useSyncExternalStore``
    // gets reference-stable returns until the slice changes.
    this._cache = {
      convList: null,
      convListV: -1,
      msgOrder: new Map(),     // conv_id -> { v, value }
      typingList: new Map(),   // conv_id -> { v, value }
      readsByUser: new Map(),  // conv_id -> { v, value }
    };
  }

  // ── Subscription plumbing ──────────────────────────────────

  subscribe = (listener) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  _notify() {
    // Snapshot the listener set first — listeners that unsubscribe
    // mid-iteration would otherwise skip the rest of the loop.
    const ls = Array.from(this._listeners);
    for (const l of ls) {
      try { l(); } catch { /* never let one bad consumer break realtime */ }
    }
  }

  _bumpMsgs(convId) {
    this.versions.msgsByConv.set(
      convId, (this.versions.msgsByConv.get(convId) || 0) + 1,
    );
  }
  _bumpTyping(convId) {
    this.versions.typingByConv.set(
      convId, (this.versions.typingByConv.get(convId) || 0) + 1,
    );
  }
  _bumpReads(convId) {
    this.versions.readsByConv.set(
      convId, (this.versions.readsByConv.get(convId) || 0) + 1,
    );
  }

  // ── Sidebar / conversation list ────────────────────────────

  setConvs(items) {
    const map = new Map();
    const order = [];
    (items || []).forEach((c) => {
      if (!c?.conversation_id) return;
      map.set(c.conversation_id, c);
      order.push(c.conversation_id);
    });
    this.state.convs = map;
    this.state.convOrder = order;
    this.versions.convs += 1;
    this._notify();
  }

  /** Patch one conv's metadata + bump it to the top of the list.
   *  No-op if the conv isn't in the current list (lifecycle race
   *  on a brand-new conv that the sidebar GET hasn't picked up
   *  yet — the next reloadList will populate it).
   */
  bumpConvPreview(convId, patch) {
    const conv = this.state.convs.get(convId);
    if (!conv) return;
    const next = new Map(this.state.convs);
    next.set(convId, { ...conv, ...patch });
    this.state.convs = next;
    const idx = this.state.convOrder.indexOf(convId);
    if (idx > 0) {
      this.state.convOrder = [
        convId,
        ...this.state.convOrder.slice(0, idx),
        ...this.state.convOrder.slice(idx + 1),
      ];
    }
    this.versions.convs += 1;
    this._notify();
  }

  patchConv(convId, patch) {
    const conv = this.state.convs.get(convId);
    if (!conv) return;
    const next = new Map(this.state.convs);
    next.set(convId, { ...conv, ...patch });
    this.state.convs = next;
    this.versions.convs += 1;
    this._notify();
  }

  // ── Messages ──────────────────────────────────────────────

  setMessages(convId, msgs) {
    const m = new Map();
    const order = [];
    (msgs || []).forEach((msg) => {
      if (!msg?.message_id) return;
      m.set(msg.message_id, msg);
      order.push(msg.message_id);
    });
    this.state.msgs.set(convId, m);
    this.state.msgOrder.set(convId, order);
    this._bumpMsgs(convId);
    this._notify();
  }

  /** Append (or no-op if already present). Used for incoming
   *  broadcasts and for optimistic ghost rows.
   */
  appendMessage(convId, msg) {
    if (!msg?.message_id) return;
    let m = this.state.msgs.get(convId);
    if (!m) m = new Map();
    if (m.has(msg.message_id)) return;
    const newM = new Map(m);
    newM.set(msg.message_id, msg);
    this.state.msgs.set(convId, newM);
    const order = this.state.msgOrder.get(convId) || [];
    this.state.msgOrder.set(convId, [...order, msg.message_id]);
    this._bumpMsgs(convId);
    this._notify();
  }

  /** Prepend a batch of older history. Used by the scroll-up
   *  paginator. ``msgs`` are assumed in chronological order.
   */
  prependMessages(convId, msgs) {
    if (!msgs || msgs.length === 0) return;
    let m = this.state.msgs.get(convId);
    if (!m) m = new Map();
    const newM = new Map(m);
    const newIds = [];
    for (const msg of msgs) {
      if (!msg?.message_id || newM.has(msg.message_id)) continue;
      newM.set(msg.message_id, msg);
      newIds.push(msg.message_id);
    }
    this.state.msgs.set(convId, newM);
    const existing = this.state.msgOrder.get(convId) || [];
    this.state.msgOrder.set(convId, [...newIds, ...existing]);
    this._bumpMsgs(convId);
    this._notify();
  }

  /** Replace an optimistic ghost (matched by client_message_id)
   *  with the real server-issued row. If no ghost is found, falls
   *  through to ``appendMessage`` so the broadcast still lands.
   */
  swapOptimistic(convId, clientMessageId, realMsg) {
    if (!realMsg?.message_id) return;
    const m = this.state.msgs.get(convId);
    if (!m) {
      this.appendMessage(convId, realMsg);
      return;
    }
    let oldId = null;
    for (const [id, msg] of m) {
      if (msg._optimistic && msg.client_message_id === clientMessageId) {
        oldId = id;
        break;
      }
    }
    if (!oldId) {
      this.appendMessage(convId, realMsg);
      return;
    }
    const newM = new Map(m);
    newM.delete(oldId);
    newM.set(realMsg.message_id, realMsg);
    this.state.msgs.set(convId, newM);
    const order = (this.state.msgOrder.get(convId) || [])
      .map((id) => (id === oldId ? realMsg.message_id : id));
    this.state.msgOrder.set(convId, order);
    this._bumpMsgs(convId);
    this._notify();
  }

  patchMessage(convId, msgId, patch) {
    const m = this.state.msgs.get(convId);
    if (!m || !m.has(msgId)) return;
    const newM = new Map(m);
    newM.set(msgId, { ...m.get(msgId), ...patch });
    this.state.msgs.set(convId, newM);
    this._bumpMsgs(convId);
    this._notify();
  }

  removeMessage(convId, msgId) {
    const m = this.state.msgs.get(convId);
    if (!m || !m.has(msgId)) return;
    const newM = new Map(m);
    newM.delete(msgId);
    this.state.msgs.set(convId, newM);
    const order = (this.state.msgOrder.get(convId) || []).filter((id) => id !== msgId);
    this.state.msgOrder.set(convId, order);
    this._bumpMsgs(convId);
    this._notify();
  }

  // ── Typing ─────────────────────────────────────────────────

  /** Bulk-set typing snapshot from the GET /typing response. */
  setTyping(convId, items) {
    const m = new Map();
    (items || []).forEach((u) => {
      if (!u?.user_type || u.user_id == null) return;
      m.set(_userKey(u.user_type, u.user_id), u.last_tick_at || new Date().toISOString());
    });
    this.state.typing.set(convId, m);
    this._bumpTyping(convId);
    this._notify();
  }

  upsertTyping(convId, userType, userId, isTyping) {
    let m = this.state.typing.get(convId) || new Map();
    const key = _userKey(userType, userId);
    const newM = new Map(m);
    if (isTyping) {
      newM.set(key, new Date().toISOString());
    } else {
      newM.delete(key);
    }
    this.state.typing.set(convId, newM);
    this._bumpTyping(convId);
    this._notify();
  }

  /** Drop typing rows whose last tick is older than ``cutoffMs``
   *  ago. Returns true if any row was removed (so the periodic
   *  cull effect can skip the notify when nothing changed).
   */
  cullTyping(convId, cutoffMs) {
    const m = this.state.typing.get(convId);
    if (!m || m.size === 0) return false;
    const cutoff = Date.now() - cutoffMs;
    const newM = new Map();
    let removed = false;
    for (const [k, iso] of m) {
      const t = iso ? Date.parse(iso) : 0;
      if (Number.isFinite(t) && t >= cutoff) newM.set(k, iso);
      else removed = true;
    }
    if (!removed) return false;
    this.state.typing.set(convId, newM);
    this._bumpTyping(convId);
    this._notify();
    return true;
  }

  // ── Read receipts ──────────────────────────────────────────

  setReads(convId, items) {
    const m = new Map();
    (items || []).forEach((r) => {
      if (!r?.user_type || r.user_id == null) return;
      m.set(_userKey(r.user_type, r.user_id), Number(r.last_read_sequence) || 0);
    });
    this.state.reads.set(convId, m);
    this._bumpReads(convId);
    this._notify();
  }

  upsertRead(convId, userType, userId, lastSeq) {
    let m = this.state.reads.get(convId) || new Map();
    const key = _userKey(userType, userId);
    if ((m.get(key) || 0) >= lastSeq) return;  // monotonic high-water
    const newM = new Map(m);
    newM.set(key, lastSeq);
    this.state.reads.set(convId, newM);
    this._bumpReads(convId);
    this._notify();
  }

  // ── Selection ──────────────────────────────────────────────

  selectConv(convId) {
    if (this.state.selectedConvId === convId) return;
    this.state.selectedConvId = convId;
    this.versions.selected += 1;
    this._notify();
  }

  // ── Snapshot helpers (cached on version) ───────────────────

  getConvList = () => {
    if (this._cache.convListV !== this.versions.convs) {
      this._cache.convList = this.state.convOrder
        .map((id) => this.state.convs.get(id))
        .filter(Boolean);
      this._cache.convListV = this.versions.convs;
    }
    return this._cache.convList;
  };

  getConv = (convId) => this.state.convs.get(convId) || null;

  getMessageOrder = (convId) => {
    const v = this.versions.msgsByConv.get(convId) || 0;
    const c = this._cache.msgOrder.get(convId);
    if (c && c.v === v) return c.value;
    const value = this.state.msgOrder.get(convId) || EMPTY_ARR;
    this._cache.msgOrder.set(convId, { v, value });
    return value;
  };

  getMessage = (convId, msgId) => {
    const m = this.state.msgs.get(convId);
    return m ? m.get(msgId) || null : null;
  };

  getTypingList = (convId) => {
    const v = this.versions.typingByConv.get(convId) || 0;
    const c = this._cache.typingList.get(convId);
    if (c && c.v === v) return c.value;
    const m = this.state.typing.get(convId);
    const value = m ? Array.from(m.entries()).map(([key, iso]) => {
      const [user_type, user_id] = key.split(":");
      return { user_type, user_id, last_tick_at: iso };
    }) : EMPTY_ARR;
    this._cache.typingList.set(convId, { v, value });
    return value;
  };

  getReadsByUser = (convId) => {
    const v = this.versions.readsByConv.get(convId) || 0;
    const c = this._cache.readsByUser.get(convId);
    if (c && c.v === v) return c.value;
    const value = this.state.reads.get(convId) || EMPTY_MAP;
    this._cache.readsByUser.set(convId, { v, value });
    return value;
  };

  getSelectedConvId = () => this.state.selectedConvId;
}

const EMPTY_ARR = Object.freeze([]);
const EMPTY_MAP = new Map();

/** Singleton store. One per app — keep it module-level so the
 *  global WebSocket (Phase B) and the page can both access it
 *  without prop-drilling.
 */
export const convStore = new ConvStore();

// Dev-only: expose for debugging in the browser console.
if (typeof window !== "undefined") {
  window.__convStore = convStore;  // eslint-disable-line no-underscore-dangle
}
